import { isEmptyObject } from '@/components/utils'
import { type PoupPriceGroupOption } from '../../components/popup-price-group/PopupPriceGroup'
import { type LocationGroup, type LocationMethod, type PriceInfo } from '../../types'
import { getAllImprintMethods } from '../use-imprint'
import { getBlankPriceList, getImprintPriceList } from '../use-price'
import { getImprintMaxQty, getImprintMinQty, getTotalQty } from '../use-quantity'
import { useWindow } from '../use-window'
import { allLocationSame, isCurrentGroup, mapSpecLimitation, mergePriceGroup } from './helper'
import {
  type PriceGroupInfo,
  type PriceGroupLocation,
  type PriceGroupLocationGroup,
  type PriceGroupLocationMethod,
  type PriceGroupMethod,
  type PriceGroupModel,
  type PriceGroupOption,
  type PriceSpecLimitation
} from './types'

export const usePriceGroup = (options: PriceGroupOption): PoupPriceGroupOption => {
  // 先加载商品基础数据
  const { productSpecList, specList, addonList, locationGroupList } = useWindow()

  // 获取所有的imprint methods
  const allImprintMethods = getAllImprintMethods()
  // 获取所有的imprint method name
  const allImprintMethodNames: string[] = [...new Set<string>(allImprintMethods.map((f) => f.Method))]
  // 根据blank/imprint 返回对应的price list
  const priceList = options.isBlank ? getBlankPriceList() : getImprintPriceList()

  const imprintMethodList = [...options.imprintMethodList]

  const initPriceGroup = (): PriceGroupInfo[] => {
    let priceGroup: PriceGroupInfo[] = priceList.map((price) => {
      return {
        priceList: [price],
        specLimitation: price.SpecLimitation,
        imprintMethodLimitation: options.imprintMethodList.length > 0 ? price.ImprintMethodLimitation : [],
        imprintLocationNum: price.ImprintLocationNum,
        minQty: price.MinQty,
        youAreHere: false,
        inGroup: false
      } as PriceGroupInfo
    })

    const allSpecLimitaion: Record<string, string[]> = {}
    // 计算新旧price list是否有交集
    productSpecList
      .map((f) => {
        return {
          name: f.name,
          values: f.values.map((f) => f.value)
        }
      })
      .concat(
        specList.map((f) => {
          return {
            name: f.name,
            values: f.values.map((f) => f.value)
          }
        }),
        addonList.map((f) => {
          return {
            name: f.name,
            values: f.values.map((f) => f.value)
          }
        })
      )
      .forEach(function (spec) {
        allSpecLimitaion[spec.name] = spec.values
      })

    // 开始合并所有price list
    priceGroup = mergePriceGroup(priceGroup, allSpecLimitaion, allImprintMethodNames)
    return priceGroup
  }

  const initPriceGroupImprint = (priceGroup: PriceGroupInfo[]) => {
    // price group里的price设置
    priceGroup.forEach((item) => {
      if (item.imprintMethodLimitation.length === 0) return

      const imprintMinQty = getImprintMinQty(
        allImprintMethods.filter((mi: any) => {
          return item.imprintMethodLimitation.includes(mi.Method)
        })
      )

      if (item.minQty < imprintMinQty) {
        item.minQty = imprintMinQty
      }
    })
    if (imprintMethodList.length > 0) {
      const imprintMinQty = getImprintMaxQty(allImprintMethods)

      priceGroup.forEach((item) => {
        if (item.imprintMethodLimitation.length > 0) return

        if (item.minQty < imprintMinQty) {
          item.imprintMethodLimitation = allImprintMethodNames
        }
      })
    }
    return priceGroup
  }

  const execute = () => {
    let priceGroup = initPriceGroup()

    // 根据值计算新的price
    let inCurrentGroup = true
    if (['productSpec', 'spec', 'addon'].includes(options.type)) {
      inCurrentGroup = isCurrentGroup(priceList, options.value.name, options.value.value)
    }

    const dataOptions: PoupPriceGroupOption = {
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      type: options.type,
      value: options.value,
      displayType: 'select',
      message: '',
      title:' ',
      switchBlankValue: false,
      priceGroupList: []
    }

    let isSelected = false

    /* 如果是imprint/blank切换 */
    if (inCurrentGroup === false || options.type === 'switch') {
      const msg: string[] = []
      let errorMsg = ''

      // 获取与当前相反的price,如果当前是blank那就取imprint price list。
      const allPrice = options.isBlank ? getImprintPriceList() : getBlankPriceList()
      if (options.type === 'switch') {
        /*
         * 计算差异
         */
        const otherPriceLst = allPrice.filter(function (price) {
          // 如果当前是imprint，那切换后肯定是blank，所以不过滤price
          if (options.isBlank === false) return true
          // 如果没有imprint limitation限制，不用过滤price
          if (price.ImprintMethodLimitation.length === 0) return true
          if (!options.imprintMethodList) return true
          // 如果没选中method，那就不用过滤条件
          if (imprintMethodList.length === 0) return true

          return imprintMethodList.every(function (method) {
            return price.ImprintMethodLimitation.includes(method.decorationMethod.Method)
          })
        })

        /* 合并所有的spec limitation */
        const otherSpecLimitation: Record<string, string[]> = {}
        otherPriceLst.forEach(function (price) {
          for (const specName in price.SpecLimitation) {
            const specValues = price.SpecLimitation[specName]
            otherSpecLimitation[specName] = (otherSpecLimitation[specName] || []).concat(specValues)
          }
        })

        /*
         * 比较差异部分
         */
        const diffObj: Record<string, string[]> = {}
        for (const key in otherSpecLimitation) {
          const values = otherSpecLimitation[key]

          options.productSpecList.forEach((item) => {
            item.productSpec.forEach((spec) => {
              if (key === spec.name && spec.value) {
                if (values.includes(spec.value.value) === false) {
                  diffObj[key] = diffObj[key] || []
                  diffObj[key].push(spec.value.value)
                }
              }
            })
          })

          // 判断spec list
          options.specList.forEach((spec) => {
            if (key === spec.name && spec.selectedItem) {
              if (values.includes(spec.selectedItem.value) === false) {
                diffObj[key] = diffObj[key] || []
                diffObj[key].push(spec.selectedItem.value)
              }
            }
          })

          // 判断add on
          options.addonList.forEach((spec) => {
            if (key === spec.name && spec.selectedItem) {
              if (values.includes(spec.selectedItem.value) === false) {
                diffObj[key] = diffObj[key] || []
                diffObj[key].push(spec.selectedItem.value)
              }
            }
          })

          // if (props.newPriceList.length === 0) {
          //   showMsg = true
          // }

          const count = 0
          const mlist: string[] = []
          for (const key in diffObj) {
            const val = diffObj[key]
            mlist.push(key + ': ' + val.join(', '))
          }

          msg.push(mlist.join('; '))
          if (count > 1) {
            msg.push(' are ')
          } else {
            msg.push(' is ')
          }
          if (options.isBlank === false) {
            msg.push('only available for items with imprint.<br />You need to re-select the option(s) after changing to Blank. Do you want to continue?')
          } else {
            msg.push('only available for items with blank.<br />You need to re-select the option(s) after changing to Imprint. Do you want to continue?')
          }
          errorMsg = msg.join('')

          // 不显示取消按钮
          dataOptions.switchBlankValue = true
          dataOptions.displayType = 'confirm'
          dataOptions.message = errorMsg
          dataOptions.title = 'Confirm'
        }
      } else {
        // showMsg = true;
        if (options.isBlank === false) {
          dataOptions.message = 'The option is only available for blank item. Do you want to change from imprint to blank?'
        } else {
          dataOptions.message = 'The option is only available for items with imprint. Do you want to change from blank to imprint?'
        }
        dataOptions.switchBlankValue = true
        dataOptions.displayType = 'confirm'
        dataOptions.title = 'Confirm'
      }
      if (dataOptions.displayType === 'confirm') {
        const isBlank = !options.isBlank
        const validPriceList = allPrice.filter((price) => {
          // 如果没有spec limitation限制,那就返回
          if (isEmptyObject(price.SpecLimitation)) return true
          return (
            options.productSpecList.every((item) => {
              // 判断每个spec是否满足price
              return item.productSpec.every(function (sp) {
                let isMatch = true
                if (price.SpecLimitation[sp.name]) {
                  // 没找到,那就返回false
                  if (price.SpecLimitation[sp.name].includes(sp.value.value) === false) {
                    isMatch = false
                  }
                }
                return isMatch
              })
            }) &&
            options.specList.every(function (sp) {
              // 判断每个specs是否满足price
              let isMatch = true
              if (price.SpecLimitation[sp.name]) {
                // 没找到,那就返回false
                if (price.SpecLimitation[sp.name].includes(sp.selectedItem?.value ?? '') === false) {
                  isMatch = false
                }
              }
              return isMatch
            }) &&
            options.addonList.every(function (sp) {
              // 判断每个specs是否满足price
              let isMatch = true
              if (price.SpecLimitation[sp.name]) {
                // 没找到,那就返回false
                if (price.SpecLimitation[sp.name].includes(sp.selectedItem?.value ?? '') === false) {
                  isMatch = false
                }
              }
              return isMatch
            })
          )
        })
        let priceInfo: PriceInfo | undefined
        let minQty = 0
        if (validPriceList.length === 0 && allPrice.length > 0) {
          priceInfo = allPrice[0]
          minQty = Math.min(
            ...allPrice.map(function (it) {
              return it.MinQty
            })
          )
        } else if (validPriceList.length > 0) {
          priceInfo = validPriceList[0]
          minQty = Math.min(
            ...validPriceList.map(function (it) {
              return it.MinQty
            })
          )
        }

        if (priceInfo) {
          // const imprintMethods = options.isBlank
          //   ? allImprintMethodNames.filter(function (method) {
          //       return (
          //         priceInfo!.ImprintMethodLimitation.length === 0 ||
          //         priceInfo!.ImprintMethodLimitation.includes(method)
          //       )
          //     })
          //   : []

          const specLimitation: PriceSpecLimitation[] = []
          for (const name in priceInfo.SpecLimitation) {
            specLimitation.push({
              name: name,
              values: priceInfo.SpecLimitation[name]
            })
          }
        }
      }
      return dataOptions
    } else {
      if (options.isBlank === false) {
        priceGroup = initPriceGroupImprint(priceGroup)
      }

      /* 判断当前在哪个分组里 开始 */
      // 先获取非 assorted 的spec name + value 的组合
      const noAssortedSpecList: Record<string, string> = {}
      options.specList.forEach((spec) => {
        if (spec.selectedItem) {
          noAssortedSpecList[spec.name] = spec.selectedItem.value
        }
      })
      options.addonList.forEach((spec) => {
        if (spec.selectedItem) {
          noAssortedSpecList[spec.name] = spec.selectedItem.value
        }
      })

      /*
       * assorted spec + 非 assorted spec,   得到最终所有spec 的组合
       * concat(store.currentItem) 用于多Spec name 尚未选择组的时候
       */
      const productSpecList = options.productSpecList
      const selectedSpecList = productSpecList.map((item) => {
        const obj = Object.assign({}, noAssortedSpecList)
        item.productSpec.forEach((it) => {
          if (it.value) {
            obj[it.name] = it.value.value
          }
        })
        return obj
      })

      // 如果Step1 没选中， Step2 or Step3 有选中
      if (productSpecList.length === 0 && !isEmptyObject(noAssortedSpecList)) {
        selectedSpecList.push(noAssortedSpecList)
      }

      // 更新inGroup数据
      priceGroup.forEach(function (value) {
        if (!options.value) return

        let inGroup = true
        let youAreHere = true
        if (isEmptyObject(value.specLimitation) === false) {
          youAreHere = selectedSpecList.every((item) => {
            for (const name in item) {
              if (item[name] !== '-1' && value.specLimitation[name] && value.specLimitation[name].includes(item[name]) === false) {
                return false
              }
            }
            return true
          })
        }

        if (options.type === 'productSpec' || options.type === 'spec' || options.type === 'addon') {
          if (isEmptyObject(value.specLimitation) === false) {
            if (options.value?.name in value.specLimitation) {
              const values = value.specLimitation[options.value?.name]
              if (values) {
                if (values.length > 0) {
                  inGroup = values.includes(options.value?.value)
                }
              }
            }
          }
        }

        if (options.isBlank === false) {
          if (value.imprintMethodLimitation.length >= 0) {
            if (imprintMethodList.length > 0) {
              if (youAreHere && value.imprintMethodLimitation.length > 0) {
                youAreHere = imprintMethodList.every((item) => {
                  return value.imprintMethodLimitation.length === 0 || value.imprintMethodLimitation.includes(item.decorationMethod.Method)
                })
              }
            }

            if (inGroup) {
              if (options.type === 'location') {
                if (options.value.location) {
                  // 查找imprint对应的location
                  inGroup = options.value.location.Methods.some((mi) => {
                    return value.imprintMethodLimitation.includes(mi.Method)
                  })
                }
              } else if (options.type === 'method') {
                // 查找imprint对应的location
                inGroup = value.imprintMethodLimitation.length === 0 || value.imprintMethodLimitation.includes(options.value.value)
              }
            }
          }
        }
        if (inGroup && isSelected === false) {
          value.selected = inGroup
          isSelected = true
        } else {
          value.selected = false
        }
        value.inGroup = inGroup // props.currentPriceList.some(f => !!value.priceList.find(k => k.PriceName === f.PriceName))
        value.youAreHere = youAreHere // options.currentPriceList.some(f => !!value.priceList.find(k => k.PriceName === f.PriceName))
      })

      // you are here显示在第一个
      priceGroup.sort((x, y) => (x.youAreHere ? -1 : 1))

      const totalQty = getTotalQty(options.productSpecList)
      /* 查找you are here的数据，判断数量是否满足条件 */
      let isBreak = false
      priceGroup.some((item) => {
        if (!options.value.location) return false
        if (!item.youAreHere) return false
        // 如果是在location上点击的。
        if (options.type === 'location') {
          if (item.imprintMethodLimitation.length > 0) {
            const isInMethods = allImprintMethods.some((it: any) => {
              return item.imprintMethodLimitation.includes(it.Method) && it.Location === options.value.location?.Name
            })
            if (!isInMethods) return false
            let minQty = Math.min(
              ...options.value.location?.Methods.map(function (it) {
                return it.MinQty
              })
            )
            // 判断当前location在哪个price name里
            const priceMinQtyList = item.priceList
              .filter(function (pr) {
                if (pr.ImprintMethodLimitation.length === 0) return true
                return pr.ImprintMethodLimitation.some(function (m) {
                  return options.value.location?.Methods.some(function (lm) {
                    return lm.Method === m
                  })
                })
              })
              .map(function (pr) {
                return pr.MinQty
              })

            minQty = Math.max(minQty, ...priceMinQtyList)

            // 判断购买
            if (totalQty < minQty) {
              dataOptions.displayType = 'confirm'
              dataOptions.showConfirmButton = true
              dataOptions.confirmButtonText = 'Go to Step1'
              dataOptions.reasonCode = 'min_qty'
              dataOptions.message = 'A minimum of ' + minQty + ' pieces (total)' + ' is required to proceed. Please, update the quantity to proceed.'
              isBreak = true
              return true
            }
          }
        } else if (options.type === 'method') {
          // 如果是在method上点击的
          if (!options.value.method) return false

          if (item.imprintMethodLimitation.length > 0) {
            if (item.imprintMethodLimitation.includes(options.value.method?.Method)) {
              // const minQty = options.value.method.MinQty

              const priceMinQtyList = item.priceList
                .filter((pr) => {
                  if (pr.ImprintMethodLimitation.length === 0) return true
                  return pr.ImprintMethodLimitation.some((m) => {
                    return m === options.value.method?.Method
                  })
                })
                .map((pr) => {
                  return pr.MinQty
                })
              const minQty = Math.max(options.value.method.MinQty, ...priceMinQtyList)

              // 判断购买
              if (totalQty < minQty) {
                dataOptions.displayType = 'confirm'
                dataOptions.confirmButtonText = 'Go to Step1'
                dataOptions.reasonCode = 'min_qty'
                dataOptions.message = 'A minimum of ' + minQty + ' pieces (total)' + ' is required to proceed. Please, update the quantity to proceed.'
                isBreak = true

                return true
              }
            }
          } else {
          }
        } else {
        }

        return false
      })

      if (isBreak) {
        // dataOptions.showConfirmButton = false
      } else {
        // 把imprint 、minqty改成 {'method name 1':minqty2,'method name 2':minqty2}
        // 这样的格式
        const objForMethodAndQty: Record<string, number> = {}
        locationGroupList.forEach(function (item) {
          item.Methods.forEach((method) => {
            objForMethodAndQty[method.Method] = method.MinQty || 0
          })
        })

        // 判断minQty值是否都一样
        let tmpMinQty = -1
        const minQtyAllSame = priceGroup.every(function (item) {
          if (tmpMinQty === -1) {
            tmpMinQty = item.minQty
          }
          return tmpMinQty === item.minQty
        })

        let methodaa: string[] | null = null
        const methodAllSame = priceGroup.every(function (item) {
          if (methodaa === null) {
            methodaa = item.imprintMethodLimitation
          }
          if (methodaa.length === 0 && item.imprintMethodLimitation.length === 0) return true
          if (methodaa.length !== item.imprintMethodLimitation.length) return false

          return methodaa.every((f) => item.imprintMethodLimitation.includes(f))
        })

        const optionPriceGroup: PriceGroupModel[] = []

        priceGroup.forEach((item) => {
          // 把imprint method显示出来
          const locationList: PriceGroupLocation[] = []

          if (imprintMethodList.length > 0) {
            if (item.imprintMethodLimitation.length > 0) {
              // 如果当前选中的是method，且选中的method不在里面，那就默认第一个method
              if (options.type === 'method') {
                // 所选的method不在里面
                if (item.imprintMethodLimitation.includes(options.value.method!.Method) === false) {
                  // 默认用第一个method
                  options.value.location!.Methods.some((mi) => {
                    if (item.imprintMethodLimitation.includes(mi.Method)) {
                      // param.method = mi;
                      return true
                    }
                    return false
                  })
                }
              }
              const imprintMethodLimitation: PriceGroupLocationMethod[] = []
              locationGroupList.forEach((imprintMethod: LocationGroup) => {
                imprintMethodLimitation.push(
                  ...imprintMethod.Methods.filter((m: LocationMethod) => item.imprintMethodLimitation.includes(m.Method)).map((imprint) => {
                    return {
                      minQty: imprint.MinQty > item.minQty ? imprint.MinQty : item.minQty,
                      location: imprint.Location,
                      method: imprint.Method
                    } as PriceGroupLocationMethod
                  })
                )
              })
              const groupByLocation: PriceGroupLocationGroup[] = []

              imprintMethodLimitation.forEach((it) => {
                let lo = groupByLocation.find((f) => f.name === it.location)
                if (lo) {
                  lo.methods.push(it)
                } else {
                  lo = {
                    name: it.location,
                    methods: [it]
                  }
                  groupByLocation.push(lo)
                }
              })

              let _minQtyAllSame = minQtyAllSame
              if (_minQtyAllSame) {
                _minQtyAllSame = imprintMethodLimitation.every((mi: any) => {
                  return mi.minQty === item.minQty
                })
              }

              // 按照相同method分组
              const groupyByMethods = groupByLocation
                .map((item) => {
                  return {
                    location: item.name,
                    methods: [...new Set(item.methods.map((val) => val.method))]
                  }
                })
                .reduce(function (prev: any, next: any) {
                  const key = next.methods.join('||')
                  prev[key] = (prev[key] || []).concat([next.location])
                  return prev
                }, {})

              // 把imprint 、minqty改成 {'method name 1':minqty2,'method name 2':minqty2}
              // 这样的格式
              const objForMethodAndQty = allImprintMethods.reduce(function (prev: any, next: any) {
                prev[next.Method] = next.MinQty || 0
                return prev
              }, {})

              // console.log('groupyByMethods', groupyByMethods, 'objForMethodAndQty', objForMethodAndQty)

              for (const methodName in groupyByMethods) {
                const val: string[] = groupyByMethods[methodName]
                const locations: PriceGroupLocation = {
                  name: [...val],
                  methods: []
                }

                methodName.split('||').forEach((m) => {
                  // 判断当前imprint 对应的minqty
                  const tempMinQty = item.priceList.filter((pr) => pr.ImprintMethodLimitation.includes(m)).map((pr) => pr.MinQty)

                  let methodMinQty = objForMethodAndQty[m]
                  methodMinQty = Math.max(methodMinQty, Math.min(...tempMinQty))

                  locations.methods.push({
                    name: m,
                    minQty: methodMinQty > item.minQty ? methodMinQty : _minQtyAllSame ? 0 : item.minQty
                  })
                })
                locationList.push(locations)
              }
            } else {
              if (methodAllSame && minQtyAllSame) {
              } else if (imprintMethodList.length > 0) {
                const locations: PriceGroupLocation = {
                  name: [],
                  methods: [
                    {
                      name: 'All are supported.',
                      minQty: 0
                    } as PriceGroupMethod
                  ]
                }
                locationList.push(locations)
              } else if (!minQtyAllSame) {
                const locations: PriceGroupLocation = {
                  name: [],
                  methods: [],
                  minQty: item.minQty
                }
                locationList.push(locations)
              }
            }
          } else {
            if (!minQtyAllSame) {
              const locations: PriceGroupLocation = {
                name: [],
                methods: [],
                minQty: item.minQty
              }
              locationList.push(locations)
            }
          }
          /* 对分组数据进行处理 */
          const locationAllSame = allLocationSame(locationGroupList, priceGroup)
          if (locationAllSame) {
            locationList.forEach((item) => {
              item.name.splice(0)
            })
          }

          optionPriceGroup.push({
            priceList: item.priceList,
            specLimitation: mapSpecLimitation(item.specLimitation),
            locations: locationList,
            imprintMethodLimitation: item.imprintMethodLimitation,
            minQty: item.minQty,
            youAreHere: item.youAreHere,
            inGroup: item.inGroup,
            selected: item.selected
          })
        })
        dataOptions.priceGroupList = optionPriceGroup
      }

      return dataOptions
    }
  }

  return execute()
}
